import React from 'react';
import { useNavigate } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

import { actionCodeSettings, auth } from '../../api/firebase/config';
import noImageIcon from '../../components/assets/noImageIcon.svg';
import {
  black,
  blueGrayText,
  grayText,
  primaryButtonBackground,
  primarycolor,
  secondaryButtonnBackground,
  tertiaryButtonBackground
} from '../../constants/Colors';
import { buttonFont } from '../../constants/Location';
import { registerStatus, role, screenType } from '../../constants/enum';
import { throwError } from '../../modules/error';

import CustomLoading from './CustomLoading';

const styles = {
  adminOnlyText: {
    color: blueGrayText,
    fontSize: '14px'
  },
  button: {
    '@media screen and (max-width:960px)': {
      height: '42px',
      width: '148px'
    },
    height: '42px',
    width: '192px'
  },
  buttonFont,
  buttonWrapper: {
    columnGap: '15px',
    display: 'flexbox',
    justifyContent: 'space-between'
  },
  contactButton: {
    '@media screen and (max-width:960px)': {
      height: '30px',
      padding: '4px 10px'
    },
    padding: '6px 16px'
  },
  contactButtonText: {
    color: black,
    fontSize: '14px'
  },
  grayText,
  mailCell: {
    maxWidth: '170px',
    wordWrap: 'break-word'
  },
  modal: {
    '@media screen and (max-width:700px)': {
      padding: '24px 16px'
    },
    boxSizing: 'border-box',
    padding: '24px 40px',
    width: '100%'
  },
  modalDesc: {
    fontSize: '16px',
    whiteSpace: 'pre-wrap'
  },
  modalText: {
    fontSize: '22px',
    padding: 0
  },
  modalTitle: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  modalWrapper: {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        '@media screen and (max-width:700px)': {
          maxWidth: '343px'
        },
        margin: 0,
        maxWidth: '676px'
      },
      '@media screen and (max-width:700px)': {
        width: '343px'
      },
      margin: '0 auto',
      width: '676px'
    }
  },
  roleCell: {
    minWidth: '70px'
  }
};

export interface StaffType {
  id: string;
  icon: string;
  name: string;
  department: string;
  role: number;
  registerStatus: number;
  email: string;
  inviteFlg: boolean;
}

interface Props {
  screen: number;
  cantEdit: boolean;
  rows: StaffType[];
}

const StaffListTable = (props: Props): React.ReactElement => {
  const { screen, rows, cantEdit } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [resendEmail, setResendEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const modalClose = (): void => {
    setModalOpen(false);
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      await sendSignInLinkToEmail(
        auth,
        resendEmail,
        actionCodeSettings(screenType.staff, resendEmail)
      );
    } catch (e) {
      throwError(e);
    } finally {
      setModalOpen(false);
      setLoading(false);
    }
  };

  return (
    <Grid container>
      <CustomLoading loading={loading} />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={styles.grayText}>{t('staff.name')}</TableCell>
            <TableCell sx={styles.grayText}>{t('staff.department')}</TableCell>
            <TableCell sx={styles.grayText} align="left">
              {t('staff.role')}
            </TableCell>
            <TableCell sx={styles.grayText} align="left">
              {t('staff.email')}
            </TableCell>
            <TableCell sx={styles.grayText} align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={index}
              sx={{
                '&:last-child td, &:last-child th': {
                  border: 0
                }
              }}
            >
              <TableCell
                sx={{
                  alignItems: 'center',
                  columnGap: '8px',
                  display: 'flex',
                  flexDirection: 'row',
                  minWidth: '150px'
                }}
              >
                <Avatar alt="logo" src={row.icon ?? noImageIcon} />
                <Typography>{row.name}</Typography>
              </TableCell>
              <TableCell sx={{ minWidth: '200px' }}>{row.department}</TableCell>
              <TableCell align="left" sx={styles.roleCell}>
                {row.role === role.member
                  ? t('staff.edit.member')
                  : t('staff.edit.admin')}
              </TableCell>
              <TableCell sx={styles.mailCell}>{row.email}</TableCell>
              <TableCell align="right" sx={{ minWidth: '150px' }}>
                {!cantEdit ? (
                  row.inviteFlg &&
                  row.registerStatus === registerStatus.emailRegistered ? (
                    <Button
                      variant="outlined"
                      sx={[tertiaryButtonBackground, styles.contactButton]}
                      onClick={() => {
                        setResendEmail(row.email);
                        setModalOpen(true);
                      }}
                    >
                      <Typography
                        component="span"
                        sx={styles.contactButtonText}
                      >
                        {t('staff.inviteMail')}
                      </Typography>
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      sx={[tertiaryButtonBackground, styles.contactButton]}
                      onClick={() => {
                        if (screen === screenType.client) {
                          navigate('/client/mypage/staff/edit', {
                            state: {
                              id: row.id
                            }
                          });
                        } else if (screen === screenType.agent) {
                          navigate('/agent/mypage/staff/edit', {
                            state: {
                              id: row.id
                            }
                          });
                        } else if (screen === screenType.publisher) {
                          navigate('/publisher/mypage/staff/edit', {
                            state: {
                              id: row.id
                            }
                          });
                        }
                      }}
                    >
                      <Typography
                        component="span"
                        sx={styles.contactButtonText}
                      >
                        {t('given.edit')}
                      </Typography>
                    </Button>
                  )
                ) : (
                  <Typography component="p" sx={styles.adminOnlyText}>
                    {t('staff.adminOnly')}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog onClose={modalClose} open={modalOpen} sx={styles.modalWrapper}>
        <DialogContent sx={styles.modal}>
          <Typography component="div" sx={styles.modalTitle}>
            <DialogTitle sx={styles.modalText}>
              {t('staff.inviteMail')}
            </DialogTitle>
            <CloseIcon onClick={modalClose} sx={{ cursor: 'pointer' }} />
          </Typography>
          <Grid container justifyContent="center" sx={{ margin: '24px 0' }}>
            <Typography component="p" sx={styles.modalDesc}>
              {resendEmail}
              {t('staff.invitemailText')}
            </Typography>
          </Grid>
          <Grid
            container
            direction="row"
            alignItems="center"
            sx={styles.buttonWrapper}
          >
            <Button
              variant="contained"
              sx={[secondaryButtonnBackground, styles.button]}
              onClick={() => {
                modalClose();
              }}
            >
              <Typography
                component="div"
                color={primarycolor}
                sx={styles.buttonFont}
              >
                {t('staff.cancel')}
              </Typography>
            </Button>
            <Button
              variant="contained"
              sx={[primaryButtonBackground, styles.button]}
              onClick={() => {
                void (async () => {
                  await onSubmit();
                })();
              }}
            >
              <Typography component="div" color="white" sx={styles.buttonFont}>
                {t('passwordReset.sendMail')}
              </Typography>
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default StaffListTable;
